<script lang="ts">
  import { untrack, type Snippet } from 'svelte';
  import { fade, fly } from 'svelte/transition';
  import ScrollShadow from './ScrollShadow.svelte';
  import { getDimensions } from '~/utils/DimensionsContext.svelte';

  const dims = getDimensions();
  let container: HTMLDivElement;
  let top: HTMLDivElement;
  let modal: HTMLDivElement;
  let modalHeight = $state(0);
  let windowWidth = $state(0);
  let scroll = $state(0);

  type Props = {
    id?: string | undefined;
    open: boolean;
    scrollOffscreen?: boolean;
    maxWidth?: number | string;
    zIndex?: number;
    onToggle: (open: boolean) => void;
    title: Snippet;
    handle: Snippet;
    content: Snippet<[{ scroll: number }]>;
    controls: Snippet;
  };

  const props: Props = $props();

  const handleKey = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleToggle();
    }
  };

  const handleToggle = () => {
    if (scroll === 0) {
      handleExpand();
    } else if (scroll === 1) {
      handleCollapse();
    }
  };

  $effect(() => {
    if (top) {
      if (props.open) {
        untrack(() => handleExpand());
      } else {
        untrack(() => handleCollapse());
      }
    }
  });

  const handleExpand = () => {
    if (scroll === 0) {
      props.onToggle(true);
      setTimeout(() => {
        modal?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 10);
    }
  };

  const handleCollapse = () => {
    if (scroll === 1) {
      props.onToggle(false);
      setTimeout(() => {
        top?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 10);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (props.open && e.key === 'Escape') {
      handleCollapse();
    }
  };

  const offsetHeight = $derived(
    props.scrollOffscreen ? 0 : dims.bottom + dims.pad / 2,
  );

  let scrollTimer: NodeJS.Timeout;
  const setScroll = () => {
    let contentHeight = modalHeight - offsetHeight;
    // account for breakpoint padding
    if (windowWidth >= 540) {
      contentHeight += dims.pad;
    }
    scroll = Math.round((100 * container.scrollTop) / contentHeight) / 100;
    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(() => {
      handleScroll(scroll!);
    }, 100);
  };

  const handleScroll = (scroll: number) => {
    if (scroll === 0 && props.open) {
      props.onToggle(false);
    } else if (scroll === 1 && !props.open) {
      props.onToggle(true);
    }
  };
</script>

<svelte:window bind:innerWidth={windowWidth} onkeydown={handleKeyDown} />
<div
  id={props.id}
  in:fade={{ duration: 500 }}
  out:fly={{ y: 20, duration: 500 }}
  class="slide-container-outer"
  class:open={props.open}
  style="
    --scroll: {scroll};
    --handle-height: {dims.bottom + dims.pad / 2}px;
    --offset-height: {offsetHeight}px;
    --board-width: {dims.board.width}px;
    --pad: {dims.pad}px;
    --z-index: {props.zIndex ?? 700};
  "
>
  <div
    class="backdrop"
    onclick={() => props.onToggle(false)}
    onkeydown={handleKey}
    role="presentation"
    class:open={props.open}
  ></div>
  <div
    class="slide-container-inner"
    style:max-width={props.maxWidth}
    onscroll={setScroll}
    bind:this={container}
  >
    <div class="spacer" bind:this={top}></div>
    <div
      class="modal"
      bind:this={modal}
      class:disabled={!props.open && props.scrollOffscreen}
    >
      <div class="modal-inner" bind:clientHeight={modalHeight}>
        <div
          class="handle"
          onclick={handleToggle}
          onkeydown={handleKey}
          role="button"
          tabindex="0"
        >
          {#if props.open}
            <div class="handle-title" in:fly={{ y: 20 }}>
              {@render props.title()}
            </div>
          {/if}
          <div class="handle-display">
            {@render props.handle()}
          </div>
        </div>
        <ScrollShadow>
          <div class="content">
            {@render props.content({ scroll })}
          </div>
        </ScrollShadow>
        <div class="controls">
          {@render props.controls()}
        </div>
      </div>
    </div>
  </div>
</div>

<style lang="scss">
  @use '~/theme/theme';
  @use '~/theme/include-media' as *;

  .slide-container-outer {
    z-index: var(--z-index);
    position: absolute;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    pointer-events: none;
  }

  .slide-container-inner {
    height: 100%;
    z-index: 2;
    width: min(100%, calc(var(--board-width) + 5 * var(--pad)));
    margin: 0 auto;
    position: relative;
    overflow: auto;
    scroll-snap-type: y mandatory;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* Internet Explorer 10+ */
    &::-webkit-scrollbar {
      /* WebKit */
      width: 0;
      height: 0;
    }
  }
  .backdrop {
    width: 100%;
    height: 100%;
    position: absolute;
    z-index: 1;
    background-color: rgba(0, 0, 0, calc(0.75 * var(--scroll)));
    &.open {
      pointer-events: all;
    }
  }
  .spacer {
    scroll-snap-align: start;
    height: calc(100vh - var(--offset-height));
    height: calc(100dvh - var(--offset-height));
    @include media('>=540px') {
      height: calc(100vh - var(--offset-height) - var(--pad));
      height: calc(100dvh - var(--offset-height) - var(--pad));
    }
  }
  .modal {
    position: relative;
    height: 100vh;
    height: 100dvh;
    pointer-events: all;
    scroll-snap-align: start;
    &.disabled {
      pointer-events: none;
    }
    // shadows
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      z-index: -1;
      width: 100%;
      height: 100vh;
      height: 100dvh;
      border-radius: 4px;
      box-shadow:
        0 4px 8px rgba(0, 0, 0, 0.2),
        0 8px 16px rgba(0, 0, 0, 0.1);
      opacity: min(1, 20 * var(--scroll));
      @include media('>=540px') {
        margin: var(--pad);
        width: calc(100% - 2 * var(--pad));
        height: calc(100vh - 2 * var(--pad));
        height: calc(100dvh - 2 * var(--pad));
      }
    }
    @include media('>=540px') {
      padding: var(--pad);
    }
  }
  .modal-inner {
    overflow: hidden;
    height: 100%;
    display: flex;
    flex-direction: column;
    background-color: theme.$background-light;
    :global(html.dark-mode) & {
      background-color: theme.$background-dark;
    }
    @include media('>480px') {
      border-radius: 4px;
    }
  }
  .handle-title {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .handle {
    pointer-events: none;
    position: relative;
    padding: 0 var(--pad);
    width: 100%;
    height: calc(2 * var(--handle-height));
    display: grid;
    grid-template-rows: 1fr 1fr;
    align-items: center;
    transform: translateY(calc(-50% * (1 - var(--scroll))));
    // white line
    & * {
      pointer-events: all;
    }
    &:before {
      content: '';
      display: block;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translateX(-50%);
      width: 1.5em;
      height: 3px;
      border-radius: 999px;
      background: currentColor;
      opacity: calc(1 - var(--scroll));
    }
    .handle-display {
      grid-row: 2;
    }
  }
  .content {
    width: 100%;
    height: 100%;
    text-align: center;
    overflow-y: auto;
    overflow-x: hidden;
  }
  .controls {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    justify-content: center;
    gap: 0.5em;
    padding: calc(var(--pad) / 2);
  }
</style>
